'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { canSpeak, onVoicesChanged, speak, speechSupported, type Speaking } from '@/lib/speak';

/**
 * Listen — the headphones under each pane, which read that pane out loud.
 *
 * ── Why it is here at all ────────────────────────────────────────────────────
 *
 * A translation you cannot pronounce is half an answer. Somebody sending a
 * Korean message to a client wants to know how it sounds before they send it,
 * and somebody learning wants to hear the sentence they have just typed. Both
 * are free in every browser (see lib/speak) and neither costs this product a
 * request.
 *
 * ── Why it can be missing, and why it is never dead ──────────────────────────
 *
 * A browser with no voice for this language cannot speak it, and no button
 * should promise otherwise: it is rendered only when there IS a voice, and the
 * check re-runs when the language changes or when the voice list finally loads
 * — Chrome answers "no voices" for the first tick of every cold page, and a
 * control that decided at mount would stay wrong until a reload.
 *
 * That is the same rule MicButton follows for the same reason: no button beats
 * a button that exists only to refuse.
 *
 * ── One voice at a time ──────────────────────────────────────────────────────
 *
 * The engine is global to the page: pressing Listen under the translation while
 * the source is still being read stops the source. Two of these on screen can
 * therefore not both be playing, and the one that is not playing shows its
 * resting state because `speak` cancels — each button hears its own `onEnd`.
 */
export default function ListenButton({
  text,
  lang,
  what,
  compact = false,
  onError,
}: {
  /** What to read. Empty disables the button by making it disappear. */
  text: string;
  /** BCP-47 tag — `ko-KR`, `en-US`. Empty means nobody knows the language yet. */
  lang: string;
  /** Named in the tooltip and for screen readers: "the text", "the translation". */
  what: string;
  /** Icon only, for the row under the answer where Copy already carries a word. */
  compact?: boolean;
  onError?: (message: string) => void;
}) {
  const [playing, setPlaying] = useState(false);
  /** Re-checked when the voice list arrives; see the note above. */
  const [available, setAvailable] = useState(false);
  const handle = useRef<Speaking | null>(null);

  useEffect(() => onVoicesChanged(() => setAvailable(canSpeak(lang))), [lang]);

  const stop = useCallback(() => {
    handle.current?.stop();
    handle.current = null;
    setPlaying(false);
  }, []);

  // Anything that changes WHAT would be read stops the reading. A voice that
  // carries on with the previous translation after the text has changed is
  // reading something that is no longer on screen.
  useEffect(() => { stop(); }, [text, lang, stop]);
  // …and leaving the page must not leave a voice talking to an empty room.
  useEffect(() => stop, [stop]);

  if (!speechSupported() || !available || !text.trim()) return null;

  const start = () => {
    handle.current = speak(text, lang, {
      onEnd: () => { handle.current = null; setPlaying(false); },
      onError: (m) => { setPlaying(false); onError?.(m); },
    });
    setPlaying(true);
  };

  const label = playing ? `Stop reading ${what}` : `Listen to ${what}`;

  return (
    <button
      type="button"
      onClick={() => (playing ? stop() : start())}
      title={label}
      aria-label={label}
      aria-pressed={playing}
      className={
        'flex shrink-0 items-center gap-1.5 rounded-full border text-[11px] font-medium transition ' +
        (compact ? 'px-2 py-1 ' : 'px-2.5 py-1 ') +
        (playing
          // The accent, not the mic's red: red on this page means "your
          // microphone is open", which is a statement about the room. This is
          // just the page talking.
          ? 'border-accent-500/70 bg-accent-500/10 text-accent-600 dark:border-accent-400/60 dark:text-accent-300'
          : 'border-ink-200 text-ink-500 hover:border-ink-300 hover:bg-ink-100 hover:text-ink-800 ' +
            'dark:border-ink-700 dark:text-ink-400 dark:hover:bg-ink-800 dark:hover:text-ink-100')
      }
    >
      {playing ? (
        /*
          Three bars that move, rather than a stop square.

          The square says what the click will do; the bars say what the page is
          doing, which is the thing somebody looks at this control to find out —
          especially with headphones not yet on, or the volume down, where the
          bars are the only evidence anything is happening at all.
        */
        <span className="flex h-3.5 w-3.5 items-end justify-center gap-[2px]" aria-hidden>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="gt-eq w-[3px] rounded-sm bg-current"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </span>
      ) : (
        // Headphones: the icon for listening, as opposed to the microphone two
        // buttons to the left, which is the icon for being listened to.
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor"
             strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M4 13v-1a8 8 0 0116 0v1" />
          <path d="M4 14.5A1.5 1.5 0 015.5 13H7v6H5.5A1.5 1.5 0 014 17.5v-3z" />
          <path d="M20 14.5a1.5 1.5 0 00-1.5-1.5H17v6h1.5a1.5 1.5 0 001.5-1.5v-3z" />
        </svg>
      )}
      {/* The word is dropped in the compact form: under the answer it sits
          beside Copy, and two labelled pills in that corner crowd out the
          timing readout on a phone. */}
      {!compact && (playing ? 'Stop' : 'Listen')}
    </button>
  );
}
